import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Task } from '../../../types';
import './GlassCardStack.css';

const ANIMATION_MS = 480;

export interface GlassCardStackProps {
  tasks: Task[];
  renderCard: (task: Task, idx: number, isActive: boolean) => React.ReactNode;
  /** Initial active task id (so toggling grid → stack returns to the previously-selected card). */
  activeTaskId?: string;
  /** Fired after each advance/reverse with the new active task id (drives .tl-dots indicator + persistence). */
  onActiveTaskChange?: (taskId: string) => void;
  /** Per-card ref-setter for FLIP transitions between view modes. */
  setItemRef?: (taskId: string) => (el: HTMLDivElement | null) => void;
  onAdvance?: () => void;
  onReverse?: () => void;
}

export const GlassCardStack: React.FC<GlassCardStackProps> = ({
  tasks, renderCard, activeTaskId, onActiveTaskChange, setItemRef, onAdvance, onReverse,
}) => {
  const [order, setOrder] = useState<number[]>(() => {
    const all = tasks.map((_, i) => i);
    if (!activeTaskId) return all;
    const activeIdx = tasks.findIndex(t => t.id === activeTaskId);
    if (activeIdx === -1) return all;
    return [...all.slice(activeIdx), ...all.slice(0, activeIdx)];
  });
  const [animating, setAnimating] = useState(false);
  const [swipingOut, setSwipingOut] = useState<number | null>(null);
  const [swipingIn, setSwipingIn] = useState<number | null>(null);
  const [noTransitionFor, setNoTransitionFor] = useState<Set<number>>(new Set());
  const animTimerRef = useRef<number | null>(null);

  // Re-sync order when tasks list changes (additions, removals)
  useEffect(() => {
    setOrder(prev => {
      const validIdxs = tasks.map((_, i) => i);
      const filtered = prev.filter(i => i < tasks.length);
      const missing = validIdxs.filter(i => !filtered.includes(i));
      return [...filtered, ...missing];
    });
  }, [tasks.length]);

  useEffect(() => () => {
    if (animTimerRef.current) window.clearTimeout(animTimerRef.current);
  }, []);

  const sleep = (ms: number) => new Promise<void>(r => {
    animTimerRef.current = window.setTimeout(r, ms);
  });
  const rAF = () => new Promise<void>(r => requestAnimationFrame(() => r()));

  const advance = useCallback(async () => {
    if (animating || order.length === 0) return;
    setAnimating(true);
    const topIdx = order[0];
    setSwipingOut(topIdx);
    await sleep(ANIMATION_MS - 20);
    setNoTransitionFor(new Set([topIdx]));
    const newOrder = [...order.slice(1), topIdx];
    setOrder(newOrder);
    setSwipingOut(null);
    await rAF();
    await rAF();
    setNoTransitionFor(new Set());
    setAnimating(false);
    onAdvance?.();
    const newActiveTaskId = tasks[newOrder[0]]?.id;
    if (newActiveTaskId) onActiveTaskChange?.(newActiveTaskId);
  }, [animating, order, tasks, onAdvance, onActiveTaskChange]);

  const reverse = useCallback(async () => {
    if (animating || order.length === 0) return;
    setAnimating(true);
    const lastIdx = order[order.length - 1];
    setNoTransitionFor(new Set([lastIdx]));
    setSwipingIn(lastIdx);
    const newOrder = [lastIdx, ...order.slice(0, -1)];
    setOrder(newOrder);
    await rAF();
    await rAF();
    setNoTransitionFor(new Set());
    setSwipingIn(null);
    await sleep(ANIMATION_MS);
    setAnimating(false);
    onReverse?.();
    const newActiveTaskId = tasks[newOrder[0]]?.id;
    if (newActiveTaskId) onActiveTaskChange?.(newActiveTaskId);
  }, [animating, order, tasks, onReverse, onActiveTaskChange]);

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>, taskIdx: number) => {
    if (taskIdx !== order[0]) return;
    if ((e.target as HTMLElement).closest('button, a')) return;
    advance();
  };

  if (tasks.length === 0) {
    return <div className="gcs-empty">All done — see your submissions in the dock below.</div>;
  }

  return (
    <div className="gcs-stage" role="region" aria-label="Task deck">
      {order.map((taskIdx, slot) => {
        const task = tasks[taskIdx];
        if (!task) return null;
        const isOut = swipingOut === taskIdx;
        const isIn = swipingIn === taskIdx;
        const skipTransition = noTransitionFor.has(taskIdx);
        const positionClass = isOut
          ? 'gcs-pos-out'
          : isIn
          ? 'gcs-pos-in-left'
          : `gcs-pos-${Math.min(slot, 3)}`;
        const isActive = slot === 0 && !isOut && !isIn;
        return (
          <div
            key={`gcs-${task.id}`}
            ref={setItemRef ? setItemRef(task.id) : undefined}
            data-task-id={task.id}
            className={`gcs-card ${positionClass}${skipTransition ? ' gcs-no-transition' : ''}`}
            role={isActive ? 'button' : undefined}
            tabIndex={isActive ? 0 : -1}
            aria-hidden={!isActive}
            aria-label={isActive ? `${task.title || task.prompt}, tap to advance` : undefined}
            onClick={(e) => handleCardClick(e, taskIdx)}
          >
            {renderCard(task, taskIdx, isActive)}
          </div>
        );
      })}
    </div>
  );
};
